import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clearAllStudentDrafts } from "../../../utils/studentDraftStorage";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  // 🧠 Save user and tokens to localStorage
  const persistUser = (userObj) => {
    // console.log("💾 Persisting user data to localStorage");
    localStorage.setItem("user", JSON.stringify(userObj));
    localStorage.setItem("accessToken", userObj.token);
    localStorage.setItem("refreshToken", userObj.refreshToken);
  };

  // ✅ Login
  const login = (userData) => {
    // console.log("🔓 Logging in with data:", userData);

    const fullUser = {
      token: userData.token,
      refreshToken: userData.refresh_token,
      user: userData.user,
    };

    setUser(fullUser);
    persistUser(fullUser);
  };

  // 🚪 Logout
  const logout = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        await fetch(`${apiUrl}/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
    } catch (err) {}

    navigate("/student/start-your-quest");
    clearStoredData();
    clearAllStudentDrafts();
    // toast.success("Logged out successfully");

    // redirectToMainLogin(); // ✅ subdomain safe
  };

  const logoutParent = async () => {
    // console.log("🚪 Logging out...");
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        await fetch(`${apiUrl}/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
        // console.log("✅ Logout API called");
      }
    } catch (err) {
      // console.error("❌ Logout error:", err.message);
    }

    clearStoredData();
    clearAllStudentDrafts();
  };
  // 🚪 Logout
  const sessionLogout = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        await fetch(`${apiUrl}/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
    } catch (err) {}

    clearStoredData();
    clearAllStudentDrafts();

    const { protocol, hostname } = window.location;
    const parts = hostname.split(".");
    const baseDomain = parts.length > 2 ? parts.slice(-2).join(".") : hostname;

    window.location.href = `${protocol}//${baseDomain}/session-expired`;
  };

  // 🧹 Clear all data
  const clearStoredData = () => {
    // console.log("🧹 Clearing stored data");
    setUser(null);
    [
      "user",
      "accessToken",
      "refreshToken",
      "mode",
      "student_ids",
      "number_of_kids",
      "currency",
      "rememberMe",
      "payment_mode",
      "student",
      "parentLocalData",
    ].forEach((key) => localStorage.removeItem(key));
  };
  const ClearUser = () => {
    setUser(null);
    localStorage.removeItem("user");
  };
  // 🔁 Refresh access token using refresh token
  const refreshAccessToken = async () => {
    // console.log("🔄 Attempting to refresh access token...");
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      console.warn("❌ No refresh token found. Logging out.");
      sessionLogout();
      return null;
    }

    try {
      const response = await fetch(`${apiUrl}/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) throw new Error("Refresh token invalid");

      const data = await response.json();
      // console.log("✅ Token refreshed successfully:", data);

      const updatedUser = {
        token: data.access_token,
        refreshToken: refreshToken,
        user: data.user,
      };
      setUser(updatedUser);
      persistUser(updatedUser);

      return data.access_token;
    } catch (err) {
      //console.error("❌ Token refresh failed:", err.message);
      sessionLogout();
      return null;
    }
  };

  // 🌐 Use this for API calls
  const fetchWithAuth = async (url, options = {}) => {
    // console.log("🌐 Calling protected API:", url);
    let accessToken = localStorage.getItem("accessToken");

    const isFormData = options.body instanceof FormData;

    let headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${accessToken}`,
    };

    // Only set JSON content type if NOT using FormData
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    let response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      console.warn("⚠️ Access token expired. Trying refresh...");
      const newToken = await refreshAccessToken();
      if (!newToken) {
        // console.error("🔴 Refresh failed. User will be logged out.");
        return null;
      }

      headers.Authorization = `Bearer ${newToken}`;

      response = await fetch(url, {
        ...options,
        headers,
      });
    }

    return response;
  };

  // 🔄 Load user on mount
  useEffect(() => {
    const tryRestoreSession = async () => {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      const refreshToken = storedUser?.refreshToken;
      if (storedUser && refreshToken) {
        try {
          const response = await fetch(`${apiUrl}/auth/refresh-token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });

          const data = await response.json();

          // Only clear storage if refresh token is truly expired/invalid
          if (
            response.status === 401 ||
            data.error === "invalid_grant" ||
            data.message === "Refresh token expired" ||
            data.resp_code === "session_expired"
          ) {
            throw new Error("Refresh token invalid or expired");
          }
          if (data.access_token || data.token) {
            const updatedUser = {
              token: data.access_token || data.token,
              refreshToken: refreshToken,
              user: data.user || storedUser.user,
            };
            setUser(updatedUser);
            persistUser(updatedUser);
          } else {
            setUser(storedUser);
          }
        } catch (err) {
          clearStoredData();
          window.location.href = "/session-expired";
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    tryRestoreSession();
  }, [apiUrl]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        fetchWithAuth,
        loading,
        logoutParent,
        ClearUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
